<template>
  <div data-qa="game-campaigns-list-wrapper">
    <rest-list
      ref="game-campaigns-table"
      :loading="isLoading || isSettlementLoading"
      :payload="fetchEndpoint"
      :data-handler="extractData"
    >
      <rest-list-column
        :title="$t('Gamification_CampaignList_Title_Id')"
        prop="id"
        :min-width="100"
      >
        <template v-slot:default="scope">
          <router-link :to="getEditGameCampaignRoute(scope.row.id)">
            {{ scope.row.id }}
          </router-link>
        </template>
      </rest-list-column>
      <rest-list-column
        prop="name"
        :title="$t('Gamification_CampaignList_Title_Name')"
        :min-width="100"
      >
        <template v-slot:default="scope">
          <router-link :to="getEditGameCampaignRoute(scope.row.id)">
            {{ scope.row.name }}
          </router-link>
        </template>
      </rest-list-column>

      <rest-list-column-enum
        prop="state"
        :enum="gameCampaignStatusEnum"
        :title="$t('Gamification_CampaignList_Status')"
        :min-width="100"
      />

      <rest-list-column
        :title="$t('Gamification_CampaignList_Title_Timezone')"
        prop="timezone"
        :min-width="100"
      />

      <rest-list-column
        :title="$t('Gamification_CampaignList_Title_Starts_At')"
        prop="starts_at"
        :sortable="true"
        :is-range="false"
        :filter-date-range="false"
        :filter-date="true"
        :date-picker-props="datePickerProps"
      >
        <template v-slot:default="scope">
          <p v-if="scope.row.starts_at">
            {{ castDate(scope.row.starts_at) }}
          </p>
        </template>
      </rest-list-column>

      <rest-list-column
        :title="$t('Gamification_CampaignList_Title_Ends_At')"
        prop="ends_at"
        :sortable="true"
        :is-range="false"
        :filter-date-range="false"
        :filter-date="true"
        :date-picker-props="datePickerProps"
      >
        <template v-slot:default="scope">
          <p v-if="scope.row.ends_at">
            {{ castDate(scope.row.ends_at) }}
          </p>
        </template>
      </rest-list-column>

      <rest-list-column-actions>
        <template v-slot:default="scope">
          <rest-list-action-view
            :route="getViewGameCampaignRoute(scope.row.id)"
            :title="$t('RestList_Action_ViewCampaign')"
          />
          <rest-list-action-update
            :route="getEditGameCampaignRoute(scope.row.id)"
          />
          <rest-list-action
            icon="el-icon-tickets"
            :click="
              () =>
                toggleCampaignSettlement(
                  scope.row.id,
                  scope.row.hold_settlement
                )
            "
            :title="
              scope.row.hold_settlement
                ? $t('Gamification_CampaignList_Action_ReleaseSettlement')
                : $t('Gamification_CampaignList_Action_HoldSettlement')
            "
          />
          <rest-list-action
            icon="el-icon-document-copy"
            :title="$t('Gamification_CampaignList_Action_Duplicate')"
            :click="
              () => {
                duplicateRow(scope.row.id, `Copy of ${scope.row.name}`)
              }
            "
            data-qa="duplicate-action-button"
          />
          <rest-list-action-delete
            :name="scope.row.name"
            :endpoint="rowDeleteEndpoint(scope.row.id)"
          />
        </template>
      </rest-list-column-actions>
    </rest-list>
    <duplicate-campaign-dialog
      v-if="isOpenDuplicateFormDialog"
      :dialog-visible="isOpenDuplicateFormDialog"
      :model="formValues"
      @close="closeDuplicateCampaignDialog"
      data-qa="duplicate-campaign-dialog"
    />
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList.vue'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListColumn from '@/common/components/rest/RestListColumn.vue'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions.vue'
import RestListActionView from '@/common/components/rest/RestListActionView.vue'
import RestListActionUpdate from '@/common/components/rest/RestListActionUpdate.vue'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete.vue'
import RestListAction from '@/common/components/rest/RestListAction.vue'
import DuplicateCampaignDialog from '@/modules/gamification/components/games-campaigns/DuplicateCampaignDialog.vue'
import RestApi from '@/core/services/RestApi'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import { gameCampaignStatusEnum } from '@/modules/gamification/enum/GameCampaignStatusEnum'
import { castDate } from '@/modules/gamification/helpers'

export default {
  name: 'GameCampaignsList',
  components: {
    RestListActionDelete,
    RestListActionUpdate,
    RestListActionView,
    RestListAction,
    RestListColumnActions,
    RestListColumn,
    RestList,
    DuplicateCampaignDialog,
    RestListColumnEnum
  },
  data() {
    return {
      isLoading: false,
      isOpenDuplicateFormDialog: false,
      formValues: {
        name: '',
        campaignId: null
      },
      isSettlementLoading: false,
      gameCampaignStatusEnum
    }
  },
  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/campaigns-list`,
        params: {
          sort: '-created_at'
        }
      })
    },
    datePickerProps() {
      return {
        type: 'datetime',
        value_format: 'yyyy-MM-dd HH:mm:ss',
        default_time: ['00:00:00']
      }
    }
  },
  methods: {
    castDate,
    extractData(responseData) {
      return responseData.data || []
    },
    getEditGameCampaignRoute(campaignId) {
      return projectRoute({
        name: 'GameEditCampaignPage',
        params: {
          id: this.$route.params.id,
          campaignId: campaignId
        }
      })
    },
    getViewGameCampaignRoute(campaignId) {
      return projectRoute({
        name: 'GameViewCampaignPage',
        params: {
          id: this.$route.params.id,
          campaignId: campaignId
        }
      })
    },
    duplicateRow(campaignId, name) {
      this.formValues = { campaignId, name }
      this.isOpenDuplicateFormDialog = true
    },
    closeDuplicateCampaignDialog(refreshList = false) {
      this.formValues = { companyId: null, name: '' }
      this.isOpenDuplicateFormDialog = false
      if (refreshList) {
        this.$refs['game-campaigns-table'].refresh(false, false)
      }
    },

    toggleCampaignSettlement(campaignId, currentHoldSettlement) {
      this.isSettlementLoading = true

      RestApi(
        projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${campaignId}/settlement`,
          data: {
            hold_settlement: !currentHoldSettlement
          }
        })
      )
        .then(() => {
          this.$message({
            message: this.$t('RestForm_SaveSuccessMessage'),
            type: 'success'
          })
          if (this.$refs['game-campaigns-table']) {
            this.$refs['game-campaigns-table'].refresh(false, true)
          }
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          this.isSettlementLoading = false
        })
    },
    rowDeleteEndpoint(campaignId) {
      return projectEndpoint({
        method: 'delete',
        url: `gamify/${this.$route.params.id}/campaign/${campaignId}`
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/GameCampaignsList.vue